/**
 * Zod schemas for the student forms (create + edit) — the last two forms
 * to leave hand-rolled validation (ADMIN-FORMS). The create page used to
 * re-derive `standaloneStartTouched` / `standaloneEndTouched` /
 * `submitAttempted` by hand to decide when a message may show. RHF now owns
 * field interaction and live validity, so actions disable consistently and
 * edited invalid fields explain themselves in place.
 *
 * Same boundary as `client-schema.ts` / `cohort-schema.ts`: **only rules
 * the client can evaluate completely, from the form's own values.** What
 * deliberately stays OUT and why:
 *
 * - whether the selected cohort has ENDED — that lives on the fetched
 *   cohort row, not in the form; it stays a computed blocker on the page.
 * - whether the email is taken — the live pre-check advises and the
 *   server's 409 decides.
 * - student credentials — operators never create or replace them; students
 *   choose their own password through an invitation/recovery flow.
 *
 * The standalone-window rules apply only when NO cohort is chosen: with a
 * cohort, both dates inherit live and the fields do not render.
 */
import { z } from 'zod'
import { companyInformationShape } from './company-information-fields'
import { tooLong } from '../common/length-message'
import {
  normalizeIdentity,
  enrollmentIdentityShape,
} from '../common/identity-schema.js'

const productTier = z.enum(['essential', 'pro', 'premium', 'unlimited'])

const createBase = z.object({
  clientId: z.string().min(1, 'Choose a client.'),
  ownership: z.enum(['company', 'private']),
  /** '' = not chosen yet; valid only for an explicitly private enrollment. */
  cohortId: z.string(),
  email: z.string().trim().min(1, 'Enter an email address.'),
  ...enrollmentIdentityShape,
  ...companyInformationShape,
  tier: productTier,
  programId: z.string(),
  trophyGoal: z.string(),
  externalPolicyRef: z.string().max(500, tooLong(500)),
  /** ISO dates, '' = unset. Only meaningful while `cohortId` is ''. */
  standaloneScheduledStartOn: z.string(),
  standaloneAccessEndsOn: z.string(),
})

/**
 * A factory because the past-date rules need TODAY, and today is a JST
 * business date — captured at validation time via the closure, not baked
 * into module state.
 *
 * One issue per field per pass (the `else if` chains): the end field can be
 * missing AND make the window reversed only in the operator's imagination —
 * queueing both messages for one mistake repeats an earlier client-form
 * validation failure.
 */
export function createStudentSchema(businessToday: string) {
  return createBase
    .superRefine((state, ctx) => {
      if (state.ownership === 'company') {
        if (state.cohortId === '') {
          ctx.addIssue({
            code: 'custom',
            path: ['cohortId'],
            message: 'Choose the company cohort for this enrollment.',
          })
        }
        return
      }

      if (state.standaloneScheduledStartOn === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['standaloneScheduledStartOn'],
          message: 'Set the first day of this enrollment.',
        })
      } else if (state.standaloneScheduledStartOn < businessToday) {
        ctx.addIssue({
          code: 'custom',
          path: ['standaloneScheduledStartOn'],
          message: 'Access must start today or later.',
        })
      }

    if (state.programId.trim() === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['programId'],
        message: 'Set the program for this private enrollment.',
      })
    }
    const trophyGoal = Number(state.trophyGoal)
    if (!Number.isInteger(trophyGoal) || trophyGoal <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['trophyGoal'],
        message: 'Set a positive whole-number trophy goal.',
      })
    }

      if (state.standaloneAccessEndsOn === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['standaloneAccessEndsOn'],
          message:
            'Set an access end date. A student without a cohort cannot have unbounded access.',
        })
      } else if (state.standaloneAccessEndsOn < businessToday) {
        ctx.addIssue({
          code: 'custom',
          path: ['standaloneAccessEndsOn'],
          message:
            'Access must end today or later. Choose a valid end date, or assign an active cohort.',
        })
      } else if (
        state.standaloneScheduledStartOn !== '' &&
        state.standaloneAccessEndsOn < state.standaloneScheduledStartOn
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['standaloneAccessEndsOn'],
          message:
            'Access cannot end before it starts. Move the start earlier or the end later.',
        })
      }
    })
    .transform(normalizeIdentity)
}

export type CreateStudentForm = z.infer<typeof createBase>

/** Identity maintenance only. Participation terms belong to enrollments. */
export const editStudentSchema = z
  .object({
    ...enrollmentIdentityShape,
  })
  .transform(normalizeIdentity)

export type EditStudentForm = z.infer<typeof editStudentSchema>
